import { generateUid, roundNumber, quantityOf } from "../helpers/formatting"
import { rollUpComposedLines, descriptionsFirst, isBreakdown, holdsLots, hasLiveChildren } from "../helpers/treeMath"

const assignUids = node => {
  if (!node) return node
  const uid = node.id ? `${node.type}-${node.id}` : generateUid(node.type || "node")
  const result = { ...node, _uid: uid, _destroy: false }

  if (node.type === "group" || (node.children || []).length > 0) {
    result._expanded = node._expanded !== false
  }

  if (node.children) {
    result.children = node.children.map(child => assignUids(child))
  }
  return result
}

export const buildInitialState = data => ({
  // Quote metadata
  quoteId:            data.new_record ? null : (data.id || null),
  number:             data.number || "",
  title:              data.title || "",
  status:             data.status || "In preparation",
  discount:           parseFloat(data.discount) || 0,
  currency:           data.currency || {},
  currencySymbol:     data.currency_symbol || "€",
  newRecord:          data.new_record || false,
  isTemplate:         data.is_template || false,
  creationDate:       data.creation_date || "",
  expirationDate:     data.expiration_date || "",
  expirationDays:     data.expiration_days != null ? Number(data.expiration_days) : 30,

  // Display info
  client:             data.client || {},
  site:               data.site || {},
  account:            data.account || {},
  contact:            data.contact || {},
  manager:            data.manager || {},

  // Settings tab options + drafts
  managers:           data.managers       || [],
  statuses:           data.statuses       || [],
  propertyFields:     (data.property_fields || []).map((f, i) => ({
    ...f,
    _uid: f.id != null ? `pf-${f.id}` : `pf-new-${i}`,
  })),
  managerId:          data.manager?.id || null,

  partners:           data.partners || [],
  partnerId:          data.partner_id || null,
  partnerFrozen:      !!data.partner_frozen,
  partnerLocked:      !!data.partner_locked,
  activeTab:          data.read_only ? "preview" : "edit",

  project:                data.project || null,

  // Permissions
  readOnly:               data.read_only || false,
  readOnlyReason:         data.read_only_reason || null,
  canPreviewPdf:          data.can_preview_pdf || false,
  canConvertToProject:    data.can_convert_to_project || false,
  readOnlyVat:            data.read_only_vat || false,
  readOnlyPurchasePrice:  data.read_only_purchase_price || false,
  showMargin:             data.show_margin || false,
  showProducts:           data.show_products || false,
  showSiteAddress:        !!data.show_site_address,
  showServiceComponents:  !!data.show_service_components,
  allowMarkup:            data.allow_markup || false,
  priceMarkupCoefficient: parseFloat(data.price_markup_coefficient) || 1,
  overheadCoefOverrides:  data.overhead_coef_overrides || {},
  saleCoefOverrides:      data.sale_coef_overrides     || {},

  // UI state
  viewMode: "sale",
  saving:   false,
  dirty:    false,

  // Tree data
  sections: descriptionsFirst(rollUpComposedLines((data.groups || []).map(section => assignUids(section)))),

  // Deleted persisted items
  deletedItems: [],

  // At most one node is ever editable at a time — prevents the stacked-Quill
  // regression from PR #9318.
  editingNode: null,

  // Only lines and descriptions can be checked: moving a lot into one of its
  // own sub-lots makes no sense.
  selection: [],

  search: { query: "", cursor: 0 },

  activeSectionUid: null,

  // Undo / redo archives the document only, never screen state.
  past:     [],
  future:   [],
  lastEdit: null,
})

// Helper: update item in array by _uid
const updateInArray = (arr, uid, updater) =>
  arr.map(item => (item._uid === uid ? updater(item) : item))

// Helper: remove item from array by _uid, tracking deletion
const removeFromArray = (arr, uid) => {
  const item = arr.find(i => i._uid === uid)
  const filtered = arr.filter(i => i._uid !== uid)
  return { filtered, removed: item }
}

const patchNode = (nodes, uid, patch) => {
  let changed = false
  const out = nodes.map(n => {
    if (changed) return n
    if (n._uid === uid) { changed = true; return patch(n) }
    for (const key of ["children", "components"]) {
      if (!n[key]) continue
      const inner = patchNode(n[key], uid, patch)
      if (inner !== n[key]) { changed = true; return { ...n, [key]: inner } }
    }
    return n
  })
  return changed ? out : nodes
}

const withOption = (node, value) => node.type !== "group" ? node : {
  ...node,
  option:   value,
  children: node.children && node.children.map(child => withOption(child, value)),
}

// All under an option is one, so lifting it also lifts it off the lots above.
const setOption = (nodes, uid, value) => {
  let found = false
  const out = nodes.map(node => {
    if (found) return node
    if (node._uid === uid) { found = true; return withOption(node, value) }
    if (!node.children) return node
    const inner = setOption(node.children, uid, value)
    if (inner === node.children) return node
    found = true
    return { ...node, children: inner, ...(value ? {} : { option: false }) }
  })
  return found ? out : nodes
}

// Collect all persisted IDs from a node and its descendants
const collectPersistedIds = node => {
  const items = []
  if (node.id) {
    items.push({ id: node.id, is_section: node.type === "group" })
  }
  if (node.children) node.children.forEach(c => items.push(...collectPersistedIds(c)))
  return items
}

const baseReducer = (state, action) => {
  switch (action.type) {
    // ── Section operations ──
    case "ADD_SECTION": {
      const { insertAfterUid, insertAtIndex, ...data } = action.payload
      const newSection = assignUids({ ...data, children: data.children || [], _focusDescription: true })
      const sections = [...state.sections]
      let target = sections.length
      if (insertAfterUid) {
        const idx = sections.findIndex(s => s._uid === insertAfterUid)
        if (idx !== -1) target = idx + 1
      } else if (insertAtIndex != null && insertAtIndex >= 0 && insertAtIndex <= sections.length) {
        target = insertAtIndex
      }
      sections.splice(target, 0, newSection)
      return { ...state, sections }
    }

    case "UPDATE_SECTION": {
      const { sectionUid, field, value } = action.payload
      return {
        ...state,
        sections: updateInArray(state.sections, sectionUid, section => ({ ...section, [field]: value })),
      }
    }

    case "DELETE_SECTION": {
      const { sectionUid } = action.payload
      const { filtered, removed } = removeFromArray(state.sections, sectionUid)
      const newDeleted = removed ? collectPersistedIds(removed) : []
      return {
        ...state,
        sections:     filtered,
        deletedItems: [...state.deletedItems, ...newDeleted],
      }
    }

    case "REORDER_SECTION": {
      const { fromIndex, toIndex } = action.payload
      const sections = [...state.sections]
      const [moved] = sections.splice(fromIndex, 1)
      sections.splice(toIndex, 0, moved)
      return { ...state, sections }
    }

    case "TOGGLE_SECTION": {
      const { sectionUid } = action.payload
      return {
        ...state,
        sections: updateInArray(state.sections, sectionUid, section => ({ ...section, _expanded: !section._expanded })),
      }
    }

    // ── Group operations ──
    case "ADD_GROUP": {
      const { sectionUid, data } = action.payload
      const newChild = assignUids({ ...data, components: data.components || [] })
      const walk = nodes =>
        nodes.map(n => {
          if (n._uid === sectionUid && n.type === "group") {
            return { ...n, _expanded: true, children: [...(n.children || []), newChild] }
          }
          if (n.children) return { ...n, children: walk(n.children) }
          return n
        })
      return { ...state, sections: walk(state.sections) }
    }

    case "ADD_LINES_TO_SECTION": {
      const { sectionUid, lines, focusLast } = action.payload
      // A hand-added line opens in edit mode; a library insert places several
      // at once, so there is no single one to point at.
      const newLines = (lines || []).map((line, index) => assignUids({
        ...line,
        type: "service",
        _focusDescription: !!focusLast && index === lines.length - 1,
      }))
      const seedVat  = state.site?.vat_rate
      const walk = nodes =>
        nodes.map(n => {
          if (n._uid === sectionUid && n.type === "group") {
            const next = { ...n, _expanded: true, children: [...(n.children || []), ...newLines] }
            if (!n.vat_rate?.id && seedVat?.id) next.vat_rate = seedVat
            return next
          }
          if (n.children) return { ...n, children: walk(n.children) }
          return n
        })
      return { ...state, sections: walk(state.sections) }
    }

    case "UPDATE_GROUP": {
      const { sectionUid, groupUid, field, value } = action.payload
      return {
        ...state,
        sections: updateInArray(state.sections, sectionUid, section => ({
          ...section,
          children: updateInArray(section.children || [], groupUid, group => {
            const updated = { ...group, [field]: value }

            // Scale line quantities proportionally when group quantity changes
            if (field === "quantity") {
              const oldQty = quantityOf(group.quantity)
              const newQty = quantityOf(value)
              // To or from 0 the lines keep theirs: scaled to 0, they could not come back.
              if (oldQty !== newQty && oldQty > 0 && newQty > 0) {
                const ratio = newQty / oldQty
                updated.components = (group.components || []).map(comp => ({
                  ...comp,
                  quantity: (parseFloat(comp.quantity) || 0) * ratio,
                }))
              }
            }

            return updated
          }),
        })),
      }
    }

    case "DELETE_GROUP": {
      const { sectionUid, groupUid } = action.payload
      return {
        ...state,
        sections: updateInArray(state.sections, sectionUid, section => {
          const { filtered, removed } = removeFromArray(section.children || [], groupUid)
          const newDeleted = removed ? collectPersistedIds(removed) : []
          return { ...section, children: filtered, _deletedChildren: newDeleted }
        }),
        deletedItems: [
          ...state.deletedItems,
          ...(() => {
            const section = state.sections.find(l => l._uid === sectionUid)
            const child = (section?.children || []).find(c => c._uid === groupUid)
            return child ? collectPersistedIds(child) : []
          })(),
        ],
      }
    }

    case "REORDER_GROUP": {
      const { sectionUid, fromIndex, toIndex } = action.payload
      return {
        ...state,
        sections: updateInArray(state.sections, sectionUid, section => {
          const children = [...(section.children || [])]
          const [moved] = children.splice(fromIndex, 1)
          children.splice(toIndex, 0, moved)
          return { ...section, children }
        }),
      }
    }

    // ── Line operations ──
    case "UPDATE_LINE": {
      const { sectionUid, groupUid, lineUid, field, value } = action.payload
      return {
        ...state,
        sections: updateInArray(state.sections, sectionUid, section => ({
          ...section,
          children: updateInArray(section.children || [], groupUid, group => ({
            ...group,
            components: updateInArray(group.components || [], lineUid, line => ({
              ...line,
              [field]: value,
            })),
          })),
        })),
      }
    }

    case "DELETE_LINE": {
      const { sectionUid, groupUid, lineUid } = action.payload
      return {
        ...state,
        sections: updateInArray(state.sections, sectionUid, section => ({
          ...section,
          children: updateInArray(section.children || [], groupUid, group => {
            const { filtered, removed } = removeFromArray(group.components || [], lineUid)
            return { ...group, components: filtered }
          }),
        })),
        deletedItems: [
          ...state.deletedItems,
          ...(() => {
            const section = state.sections.find(l => l._uid === sectionUid)
            const group = (section?.children || []).find(o => o._uid === groupUid)
            const line = (group?.components || []).find(c => c._uid === lineUid)
            return line?.id ? [{ id: line.id, is_section: false, is_subtotal: false }] : []
          })(),
        ],
      }
    }

    case "REORDER_LINE": {
      const { sectionUid, groupUid, fromIndex, toIndex } = action.payload
      return {
        ...state,
        sections: updateInArray(state.sections, sectionUid, section => ({
          ...section,
          children: updateInArray(section.children || [], groupUid, group => {
            const components = [...(group.components || [])]
            const [moved] = components.splice(fromIndex, 1)
            components.splice(toIndex, 0, moved)
            return { ...group, components }
          }),
        })),
      }
    }

    case "SET_GROUP_VAT": {
      const { sectionUid, groupUid, vatRate } = action.payload
      return {
        ...state,
        sections: updateInArray(state.sections, sectionUid, section => ({
          ...section,
          children: updateInArray(section.children || [], groupUid, group => ({
            ...group,
            components: (group.components || []).map(comp => ({ ...comp, vat_rate: vatRate })),
          })),
        })),
      }
    }

    // ── Generic field update on any node by _uid ──
    case "UPDATE_NODE": {
      const { nodeUid, field, value, fields } = action.payload
      const patch = fields || { [field]: value }
      return { ...state, sections: patchNode(state.sections, nodeUid, n => ({ ...n, ...patch })) }
    }

    case "SET_OPTION": {
      const { nodeUid, value } = action.payload
      return { ...state, sections: setOption(state.sections, nodeUid, value) }
    }

    // ── Begin edit session on a node (Quill single-instance guard) ──
    case "BEGIN_EDIT": {
      if (state.editingNode && state.editingNode.nodeUid !== action.payload.nodeUid) {
        return state
      }
      return { ...state, editingNode: action.payload }
    }

    case "CANCEL_EDIT": {
      return { ...state, editingNode: null }
    }

    case "APPLY_EDIT": {
      const { nodeUid, field, value } = action.payload
      const sections = patchNode(state.sections, nodeUid, n => ({ ...n, [field]: value }))
      return { ...state, sections, editingNode: null }
    }

    // ── Toggle expand/collapse any node by _uid ──
    case "TOGGLE_NODE": {
      const { nodeUid } = action.payload
      return { ...state, sections: patchNode(state.sections, nodeUid, n => ({ ...n, _expanded: !n._expanded })) }
    }

    // ── Expand or collapse every aggregate node ──
    case "SET_ALL_EXPANDED": {
      const expanded = !!action.payload?.expanded
      const walk = nodes =>
        nodes.map(n => {
          if (isBreakdown(n) && !(n.children || []).length) return n
          const next = { ...n, _expanded: expanded }
          if (n.children)   next.children   = walk(n.children)
          if (n.components) next.components = walk(n.components)
          return next
        })
      return { ...state, sections: walk(state.sections) }
    }

    case "SCALE_GROUP_QTY": {
      const { groupUid, quantity, unit } = action.payload
      const newQty = quantityOf(quantity)
      const walk = nodes =>
        nodes.map(n => {
          if (n._uid === groupUid && n.type === "group") {
            const oldQty = quantityOf(n.quantity)
            // To or from 0 the lines keep theirs: scaled to 0, they could not come back.
            const ratio = oldQty > 0 && newQty > 0 ? newQty / oldQty : 1
            const scaled = (n.components || []).map(c => ({
              ...c,
              quantity: +(((parseFloat(c.quantity) || 0) * ratio) || 0).toFixed(3),
            }))
            return {
              ...n,
              quantity: newQty,
              ...(unit !== undefined ? { unit } : {}),
              components: scaled,
            }
          }
          if (n.children)   return { ...n, children:   walk(n.children) }
          if (n.components) return { ...n, components: walk(n.components) }
          return n
        })
      return { ...state, sections: walk(state.sections) }
    }

    // ── Reorder children at any depth ──
    case "REORDER_NODE": {
      const { parentUid, fromIndex, toIndex } = action.payload
      if (fromIndex === toIndex) return state
      if (!parentUid) {
        const sections = [...state.sections]
        const [moved] = sections.splice(fromIndex, 1)
        sections.splice(toIndex, 0, moved)
        return { ...state, sections }
      }
      const walk = nodes =>
        nodes.map(n => {
          if (n._uid === parentUid) {
            const arr = [...(n.children || [])]
            const [moved] = arr.splice(fromIndex, 1)
            arr.splice(toIndex, 0, moved)
            return { ...n, children: arr }
          }
          if (n.children) return { ...n, children: walk(n.children) }
          return n
        })
      return { ...state, sections: walk(state.sections) }
    }

    // ── Duplicate any node anywhere in the tree ──
    case "DUPLICATE_NODE": {
      const { nodeUid } = action.payload
      const freshClone = src => {
        const tempUid = `${src.type || "node"}-temp-${crypto.randomUUID()}`
        const clone = { ...src, _uid: tempUid, id: undefined, _destroy: false }
        if (src.children)   clone.children   = src.children.map(freshClone)
        if (src.components) clone.components = src.components.map(freshClone)
        return clone
      }
      const walk = nodes => {
        const out = []
        for (const n of nodes) {
          if (n._uid === nodeUid) {
            out.push(n, freshClone(n))
            continue
          }
          const next = { ...n }
          if (n.children)   next.children   = walk(n.children)
          if (n.components) next.components = walk(n.components)
          out.push(next)
        }
        return out
      }
      return { ...state, sections: walk(state.sections) }
    }

    // ── Delete any node anywhere in the tree ──
    case "DELETE_NODE": {
      const { nodeUid } = action.payload
      const removedQlines = []
      const walk = nodes => {
        const out = []
        for (const n of nodes) {
          if (n._uid === nodeUid) {
            if (isBreakdown(n)) {
              if (n.id) out.push({ ...n, _destroy: true })
              continue
            }
            if (n.id) {
              removedQlines.push({ id: n.id, is_section: n.type === "group" })
            }
            continue
          }
          const next = { ...n }
          if (n.children)   next.children   = walk(n.children)
          if (n.components) next.components = walk(n.components)
          out.push(next)
        }
        return out
      }
      const sections = walk(state.sections)
      return {
        ...state,
        sections,
        deletedItems: [...state.deletedItems, ...removedQlines],
      }
    }

    case "ADD_DESCRIPTION": {
      const { parentUid } = action.payload
      const description = assignUids({ type: "descriptive", description: "", _focusDescription: true })
      let placed = false
      const insert = nodes =>
        nodes.map(n => {
          if (n._uid === parentUid && n.type === "group" && !holdsLots(n)) {
            placed = true
            return { ...n, _expanded: true, children: [...(n.children || []), description] }
          }
          if (n.children) return { ...n, children: insert(n.children) }
          return n
        })
      const sections = insert(state.sections)
      return placed ? { ...state, sections } : state
    }

    // The new line inherits the category, the unit and the VAT of its neighbour.
    case "ADD_SIBLING_LINE": {
      const { nodeUid } = action.payload
      let inserted = false

      const walk = nodes => {
        const index = nodes.findIndex(n => n._uid === nodeUid)
        if (index !== -1) {
          inserted = true
          const model = nodes[index]
          const line  = assignUids({
            type:                "service",
            description:         "",
            cost_category:       model.cost_category || "materials",
            quantity:            1,
            unit:                model.unit || "",
            unit_price:          0,
            purchase_price:      null,
            discount:            0,
            coefficient:         1,
            ref_product_distrib: "",
            vat_rate:            model.vat_rate || state.site?.vat_rate || {},
            currency:            state.currency || {},
            currency_symbol:     state.currencySymbol || "€",
            _focusDescription:   true,
          })
          return [...nodes.slice(0, index + 1), line, ...nodes.slice(index + 1)]
        }
        return nodes.map(n => (n.children ? { ...n, children: walk(n.children) } : n))
      }

      const sections = walk(state.sections)
      return inserted ? { ...state, sections } : state
    }

    // A qline holds either qlines or breakdowns, never both.
    case "INSERT_BLUEPRINTS": {
      const { parentUid, blueprints, asLines } = action.payload || {}
      if (!parentUid || !(blueprints || []).length) return state

      const vatRate = state.site?.vat_rate || {}
      const lineDefaults = {
        type:            "service",
        discount:        0,
        coefficient:     1,
        vat_rate:        vatRate,
        currency:        state.currency || {},
        currency_symbol: state.currencySymbol || "€",
      }
      const linesOf = blueprint => [
        ...(blueprint.children || []).map(composedLine),
        ...(blueprint.lines || []).map(line => ({ ...lineDefaults, ...line })),
      ]
      const composedLine = blueprint => ({
        ...lineDefaults,
        cost_category:       "other",
        description:         blueprint.name || blueprint.description || "",
        ref_product_distrib: blueprint.ref || blueprint.ref_product_distrib || "",
        unit:                blueprint.unit || "",
        quantity:            blueprint.quantity ?? 1,
        unit_price:          blueprint.unit_price || 0,
        purchase_price:      blueprint.purchase_price || 0,
        children:            linesOf(blueprint),
      })
      const lotOf = blueprint => ({
        type:                "group",
        description:         blueprint.name || blueprint.description || "",
        ref_product_distrib: blueprint.ref || blueprint.ref_product_distrib || "",
        unit:                blueprint.unit || "",
        quantity:            blueprint.quantity ?? 1,
        vat_rate:            vatRate,
        _expanded:           true,
        children:            linesOf(blueprint),
      })

      let placed = false

      const walk = nodes => {
        const out = []
        for (const node of nodes) {
          if (node._uid === parentUid && node.type === "group") {
            placed = true
            const live       = (node.children || []).filter(c => !c._destroy)
            const holdsLines = live.some(c => c.type === "service") || (!holdsLots(node) && live.some(isBreakdown))
            const build      = asLines || holdsLines ? composedLine : lotOf
            const built      = blueprints.map(blueprint => assignUids(build(blueprint)))
            out.push({ ...node, _expanded: true, children: [...(node.children || []), ...built] })
            continue
          }
          out.push(node.children ? { ...node, children: walk(node.children) } : node)
        }
        return out
      }

      const sections = walk(state.sections)
      return placed ? { ...state, sections } : state
    }

    case "TOGGLE_SELECTION": {
      const { uid } = action.payload || {}
      if (!uid) return state
      const selection = state.selection.includes(uid)
        ? state.selection.filter(u => u !== uid)
        : [...state.selection, uid]
      return { ...state, selection }
    }

    case "SET_SELECTION": {
      const uids = action.payload?.uids || []
      return { ...state, selection: [...new Set(uids)] }
    }

    case "CLEAR_SELECTION":
      return state.selection.length === 0 ? state : { ...state, selection: [] }

    // A line or a description is a `qline_breakdown`, sent marked `_destroy`, so
    // it stays in the tree; a lot is a `qline`, whose id goes to the deletions.
    case "DELETE_SELECTED": {
      const doomed = new Set(state.selection)
      if (doomed.size === 0) return state

      const removedQlines = []
      const walk = nodes => nodes.flatMap(n => {
        if (doomed.has(n._uid)) {
          if (isBreakdown(n)) return n.id ? [{ ...n, _destroy: true }] : []
          if (n.id) removedQlines.push({ id: n.id, is_section: n.type === "group" })
          return []
        }
        if (!n.children) return [n]
        return [{ ...n, children: walk(n.children) }]
      })

      return {
        ...state,
        sections:     walk(state.sections),
        deletedItems: [...state.deletedItems, ...removedQlines],
        selection:    [],
      }
    }

    case "DUPLICATE_SELECTED": {
      const chosen = new Set(state.selection)
      if (chosen.size === 0) return state

      const copies = []
      const clone = source => {
        const uid = `${source.type || "node"}-temp-${crypto.randomUUID()}`
        const copy = { ...source, _uid: uid, id: undefined, _destroy: false, _focusDescription: false }
        if (source.children) copy.children = source.children.map(clone)
        return copy
      }

      const walk = nodes => nodes.flatMap(n => {
        if (chosen.has(n._uid)) {
          const copy = clone(n)
          copies.push(copy._uid)
          return [n, copy]
        }
        if (!n.children) return [n]
        return [{ ...n, children: walk(n.children) }]
      })

      return { ...state, sections: walk(state.sections), selection: copies }
    }

    // A saved line changing qline goes anew: nested attributes only look an id
    // up among its qline's own lines.
    case "MOVE_SELECTED": {
      const { targetUid } = action.payload || {}
      const chosen = new Set(state.selection)
      if (!targetUid || chosen.size === 0) return state

      const renamed = {}
      const fresh = node => {
        const copy = { ...node, _uid: generateUid(node.type || "node"), id: undefined, _destroy: false, _focusDescription: false }
        renamed[node._uid] = copy._uid
        if (node.children) copy.children = node.children.filter(child => !child._destroy).map(fresh)
        return copy
      }

      const moved = []
      const lift = (nodes, qlineUid) => nodes.flatMap(n => {
        if (chosen.has(n._uid) && !n._destroy) {
          if (isBreakdown(n) && n.id && qlineUid !== targetUid) {
            moved.push(fresh(n))
            return [{ ...n, _destroy: true }]
          }
          moved.push(n)
          return []
        }
        if (!n.children) return [n]
        return [{ ...n, children: lift(n.children, n.type === "group" ? n._uid : qlineUid) }]
      })

      const lifted = lift(state.sections, null)
      if (moved.length === 0) return state

      let landed = false
      const drop = nodes => nodes.map(n => {
        if (n._uid === targetUid && n.type === "group") {
          landed = true
          return { ...n, _expanded: true, children: [...(n.children || []), ...moved] }
        }
        if (!n.children) return n
        return { ...n, children: drop(n.children) }
      })

      const sections  = drop(lifted)
      const selection = state.selection.map(uid => renamed[uid] || uid)
      return landed ? { ...state, sections, selection } : state
    }

    case "SET_SEARCH": {
      const query = String(action.payload?.query ?? "")
      if (query === state.search.query) return state
      return { ...state, search: { query, cursor: 0 } }
    }

    case "MOVE_SEARCH_CURSOR": {
      const { total, delta } = action.payload || {}
      if (!total) return state
      const cursor = ((state.search.cursor + delta) % total + total) % total
      if (cursor === state.search.cursor) return state
      return { ...state, search: { ...state.search, cursor } }
    }

    case "SET_ACTIVE_SECTION": {
      const { sectionUid } = action.payload || {}
      if (state.activeSectionUid === sectionUid) return state
      return { ...state, activeSectionUid: sectionUid || null }
    }

    case "ADD_SUBSECTION": {
      const { parentUid, data } = action.payload
      const newSection = {
        ...data,
        _uid:                `section-temp-${Date.now()}`,
        _destroy:            false,
        _expanded:           true,
        _focusDescription:   true,
        type:             "group",
        children:            data.children || [],
      }
      const insert = nodes =>
        nodes.map(n => {
          if (n._uid === parentUid) {
            return { ...n, _expanded: true, children: [...(n.children || []), newSection] }
          }
          if (n.children) {
            return { ...n, children: insert(n.children) }
          }
          return n
        })
      return { ...state, sections: insert(state.sections) }
    }

    case "TOGGLE_GROUP": {
      const { sectionUid, groupUid } = action.payload
      return {
        ...state,
        sections: updateInArray(state.sections, sectionUid, section => ({
          ...section,
          children: updateInArray(section.children || [], groupUid, group => ({
            ...group,
            _expanded: !group._expanded,
          })),
        })),
      }
    }

    // ── UI state ──
    case "SET_VIEW_MODE":
      return { ...state, viewMode: action.payload }

    case "SET_TAB":
      return { ...state, activeTab: action.payload }

    // The server knows nothing of the user's folding; the uid of a saved node
    // is stable (`group-12`), so it can be handed back to it.
    case "RESET_FROM_SERVER": {
      const folded = new Set()
      const readFold = nodes => (nodes || []).forEach(n => {
        if (n._expanded === false) folded.add(n._uid)
        readFold(n.children)
      })
      readFold(state.sections)

      const applyFold = nodes => (nodes || []).map(n => {
        const next = folded.has(n._uid) ? { ...n, _expanded: false } : n
        return next.children ? { ...next, children: applyFold(next.children) } : next
      })

      const fresh = buildInitialState(action.payload)
      return {
        ...fresh,
        sections:  applyFold(fresh.sections),
        activeTab: state.activeTab,
        viewMode:  state.viewMode,
      }
    }

    case "SET_QUOTE_FIELD":
      return { ...state, [action.payload.field]: action.payload.value }

    case "UPDATE_PROPERTY_FIELD": {
      const { uid, field, value } = action.payload
      return {
        ...state,
        propertyFields: state.propertyFields.map(f =>
          f._uid === uid ? { ...f, [field]: value } : f
        ),
      }
    }

    case "SET_SAVING":
      return { ...state, saving: action.payload }

    case "SET_DISCOUNT":
      return { ...state, discount: parseFloat(action.payload) || 0 }

    case "UPDATE_TITLE":
      return { ...state, title: action.payload }

    // Patch multiple top-level fields in one go (client, site, dates…),
    // with optional cascade of VAT rate / discount to every component line.
    case "UPDATE_QUOTE_META": {
      const { fields = {}, cascade } = action.payload || {}
      let next = { ...state, ...fields }
      if (cascade && (cascade.vat_rate || cascade.discount != null)) {
        const walk = nodes =>
          nodes.map(n => {
            if (n.type === "service") {
              const line = { ...n }
              if (cascade.vat_rate && cascade.vat_rate.id) line.vat_rate = cascade.vat_rate
              if (cascade.discount != null && cascade.discount > 0) line.discount = cascade.discount
              return line
            }
            if (n.children)   return { ...n, children:   walk(n.children) }
            if (n.components) return { ...n, components: walk(n.components) }
            return n
          })
        next = { ...next, sections: walk(state.sections) }
      }
      return next
    }

    // A policy may be set on a still empty quote: a change carrying no sale
    // coefficient rewrites no line, but its overhead coefficient is stored.
    case "APPLY_CATEGORY_COEFS_BULK": {
      const changes = (action.payload?.changes || []).filter(Boolean)
      if (changes.length === 0) return state

      const priced = changes.filter(change => change.saleCoef > 0)
      const changeByCategory = {}
      priced.forEach(change => { changeByCategory[change.category] = change })
      const walk = nodes =>
        nodes.map(n => {
          if (n.type === "service") {
            // A composed line takes its price from its own lines, rolled up afterwards.
            if (hasLiveChildren(n)) return { ...n, children: walk(n.children) }
            const change = changeByCategory[n.cost_category || "materials"]
            if (!change) return n
            const purchase = parseFloat(n.purchase_price) || 0
            if (purchase <= 0) return n
            // Selling price = purchase price × (1 + overhead coef) × sale coef.
            const overheadCoef = Number.isFinite(change.overheadCoef) ? change.overheadCoef : 0
            const costPrice    = purchase * (1 + overheadCoef)
            return {
              ...n,
              unit_price:       roundNumber(costPrice * change.saleCoef),
              _pricedByPolicy:  true,
            }
          }
          if (n.children)   return { ...n, children:   walk(n.children) }
          if (n.components) return { ...n, components: walk(n.components) }
          return n
        })
      const saleCoefOverrides     = { ...state.saleCoefOverrides }
      const overheadCoefOverrides = { ...state.overheadCoefOverrides }
      changes.forEach(change => {
        if (change.saleCoef > 0) saleCoefOverrides[change.category] = change.saleCoef
        if (Number.isFinite(change.overheadCoef)) overheadCoefOverrides[change.category] = change.overheadCoef
      })

      return {
        ...state,
        sections: priced.length > 0 ? walk(state.sections) : state.sections,
        saleCoefOverrides,
        overheadCoefOverrides,
        dirty: true,
      }
    }

    default:
      return state
  }
}

const NON_DIRTYING_ACTIONS = new Set([
  "SET_SAVING",
  "SET_VIEW_MODE",
  "SET_TAB",
  "BEGIN_EDIT",
  "CANCEL_EDIT",
  "TOGGLE_NODE",
  "TOGGLE_SECTION",
  "TOGGLE_GROUP",
  "SET_ALL_EXPANDED",
  "SET_ACTIVE_SECTION",
  "SET_SEARCH",
  "MOVE_SEARCH_CURSOR",
  "TOGGLE_SELECTION",
  "SET_SELECTION",
  "CLEAR_SELECTION",
  "RESET_FROM_SERVER",
  "MARK_CLEAN",
])

// Two consecutive keystrokes in the same cell are coalesced: typing "1", "2",
// "5" in one cell is a single gesture and a single undo must undo it.
const HISTORY_LIMIT = 60
const COALESCE_MS   = 700

const FIELD_EDITS = new Set(["UPDATE_NODE", "UPDATE_LINE", "UPDATE_GROUP", "UPDATE_SECTION"])

const snapshotOf = state => ({ sections: state.sections, deletedItems: state.deletedItems })

const documentChanged = (before, after) =>
  before.sections !== after.sections || before.deletedItems !== after.deletedItems

// Fields starting with "_" are display flags, not quote data: counting them
// would dirty the quote and create invisible undo steps.
const isDisplayOnly = action => {
  if (action.type !== "UPDATE_NODE") return false
  const { fields, field } = action.payload || {}
  const keys = fields ? Object.keys(fields) : (field ? [field] : [])
  return keys.length > 0 && keys.every(key => key.startsWith("_"))
}

const editSignature = action => {
  if (!FIELD_EDITS.has(action.type)) return null
  const p = action.payload || {}
  const uid = p.nodeUid || p.lineUid || p.groupUid || p.sectionUid
  if (!uid) return null
  const fields = p.fields ? Object.keys(p.fields).sort().join(",") : (p.field || "")
  return `${action.type}:${uid}:${fields}`
}

const continuesLastEdit = (state, signature) =>
  !!signature &&
  !!state.lastEdit &&
  state.lastEdit.signature === signature &&
  Date.now() - state.lastEdit.at < COALESCE_MS

// Nodes that came out of an action unchanged get their previous object back, so their rows skip rendering.
const indexByUid = (nodes, index = new Map()) => {
  ;(nodes || []).forEach(node => {
    if (node._uid) index.set(node._uid, node)
    if (node.children)   indexByUid(node.children, index)
    if (node.components) indexByUid(node.components, index)
  })
  return index
}

const NESTED = new Set(["children", "components"])

const sameFields = (a, b) => {
  const keys = Object.keys(a)
  if (keys.length !== Object.keys(b).length) return false
  return keys.every(key => NESTED.has(key) || (key in b && a[key] === b[key]))
}

const shareList = (list, previousList, index) => {
  if (!Array.isArray(list)) return list
  const shared = list.map((node, i) => (node === previousList?.[i] ? node : shareNode(node, previousList?.[i], index)))
  if (Array.isArray(previousList) && previousList.length === shared.length &&
      shared.every((node, i) => node === previousList[i])) return previousList
  return shared.every((node, i) => node === list[i]) ? list : shared
}

const shareNode = (node, inPlace, index) => {
  if (!node || typeof node !== "object") return node
  const previous   = inPlace && inPlace._uid === node._uid ? inPlace : index().get(node._uid)
  const children   = shareList(node.children,   previous?.children,   index)
  const components = shareList(node.components, previous?.components, index)
  if (previous && previous !== node && sameFields(previous, node) &&
      children === previous.children && components === previous.components) return previous
  if (children === node.children && components === node.components) return node
  const next = { ...node }
  if (node.children)   next.children   = children
  if (node.components) next.components = components
  return next
}

export const shareUnchanged = (previous, next) => {
  if (previous === next) return next
  let built = null
  const index = () => (built ||= indexByUid(previous))
  return shareList(next, previous, index)
}

const withSettledTree = (before, next) => {
  if (next === before || next.sections === before.sections) return next
  // The tree before the action is already settled: only what changed is recomputed.
  const shared   = shareUnchanged(before.sections, next.sections)
  const sections = descriptionsFirst(rollUpComposedLines(shared, before.sections), before.sections)
  return sections === next.sections ? next : { ...next, sections }
}

export const quoteReducer = (state, action) => {
  if (action.type === "MARK_CLEAN") return { ...state, dirty: false }

  if (action.type === "UNDO") {
    if (state.past.length === 0) return state
    const previous = state.past[state.past.length - 1]
    return {
      ...state,
      ...previous,
      past:     state.past.slice(0, -1),
      future:   [snapshotOf(state), ...state.future].slice(0, HISTORY_LIMIT),
      lastEdit: null,
      dirty:    true,
    }
  }

  if (action.type === "REDO") {
    if (state.future.length === 0) return state
    const [next, ...rest] = state.future
    return {
      ...state,
      ...next,
      past:     [...state.past, snapshotOf(state)].slice(-HISTORY_LIMIT),
      future:   rest,
      lastEdit: null,
      dirty:    true,
    }
  }

  const before = state
  const next   = withSettledTree(state, baseReducer(state, action))
  if (next === state) return state

  // A server round trip starts from a fresh tree with fresh ids, so undoing
  // past a save would recreate already written lines.
  if (action.type === "RESET_FROM_SERVER") {
    return { ...next, past: [], future: [], lastEdit: null }
  }

  if (NON_DIRTYING_ACTIONS.has(action.type) || isDisplayOnly(action)) return next

  const dirtied = next.dirty ? next : { ...next, dirty: true }
  if (!documentChanged(before, next)) return dirtied

  const signature = editSignature(action)
  const stamp     = signature ? { signature, at: Date.now() } : null

  if (continuesLastEdit(before, signature)) {
    return { ...dirtied, lastEdit: stamp }
  }

  return {
    ...dirtied,
    past:     [...before.past, snapshotOf(before)].slice(-HISTORY_LIMIT),
    future:   [],
    lastEdit: stamp,
  }
}
